import {
	totalsActual,
	totalsDifference,
	totalsEstimate,
	totalsPlanned,
	totalsProjected,
	totalsRemaining,
	totalsReported,
} from '../renders/TaskTotalsHeaders';
import React from 'react';
import {ESTIMATION_UNIT} from '../../../../../constants';
import {useIntl} from 'react-intl';
import {hasFeatureFlag} from '../../../../shared/util/FeatureUtil';

export const useGroupColumns = customFieldColumnCount => {
	const intl = useIntl();
	const hasFinancialCategoriesUpdate = hasFeatureFlag('financial_categories_update');

	const columns = [
		{
			key: 'name',
			Value: ({row}) => <span>{row && (row.generatedName || row.name)}</span>,
		},
		{key: 'indicator'},
		{key: 'project'},
		{key: 'project_deadline'},
		{key: 'project_stage'},
		{key: 'rateCard'},
		{key: 'client'},
		{key: 'task_startDate'},
		{key: 'task_endDate'},
		{key: 'task_createdAt'},
		{key: 'role'},
		{key: 'assignees'},
		{key: 'status'},
		{key: 'phase'},
		{key: 'phase_startDate'},
		{key: 'phase_endDate'},
		{key: 'sprint'},
		{key: 'sprint_startDate'},
		{key: 'sprint_endDate'},
		{key: 'progress'},
		{
			key: 'estimate_hours',
			Value: ({row}) => row.taskTotals?.work?.estimate && totalsEstimate(row.taskTotals, intl, ESTIMATION_UNIT.HOURS),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			key: 'estimate_points',
			Value: ({row}) => row.taskTotals?.work?.estimate && totalsEstimate(row.taskTotals, intl, ESTIMATION_UNIT.POINTS),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			key: 'projected',
			Value: ({row}) => row.taskTotals?.reported?.projected && totalsProjected(row.taskTotals, intl),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			key: 'reported',
			Value: ({row}) => row.taskTotals?.reported && totalsReported(row.taskTotals, intl),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			key: 'remaining_hours',
			Value: ({row}) => row.taskTotals?.work?.remaining && totalsRemaining(row.taskTotals, intl, ESTIMATION_UNIT.HOURS),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			key: 'remaining_points',
			Value: ({row}) => row.taskTotals?.work?.remaining && totalsRemaining(row.taskTotals, intl, ESTIMATION_UNIT.POINTS),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			key: 'difference_hours',
			Value: ({row}) =>
				row.taskTotals?.work?.estimate &&
				row.taskTotals?.reported &&
				totalsDifference(row.taskTotals, intl, ESTIMATION_UNIT.HOURS),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			key: 'difference_points',
			Value: ({row}) =>
				row.taskTotals?.work?.estimate &&
				row.taskTotals?.reported &&
				totalsDifference(row.taskTotals, intl, ESTIMATION_UNIT.POINTS),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			key: 'planned_revenue',
			Value: ({row}) => row.taskTotals?.financial?.revenue && totalsPlanned(row.taskTotals, row.currency, intl),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			key: 'actual_revenue',
			Value: ({row}) => row.taskTotals?.financial?.revenue && totalsActual(row.taskTotals, row.currency, intl),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{key: 'hours-estimated', showCondition: hasFinancialCategoriesUpdate},
		{key: 'hours-actual', showCondition: hasFinancialCategoriesUpdate},
		{key: 'hours-actual-billable', showCondition: hasFinancialCategoriesUpdate},
		{key: 'hours-actual-non-billable', showCondition: hasFinancialCategoriesUpdate},
		{key: 'remaining-work-hours', showCondition: hasFinancialCategoriesUpdate},
		{key: 'projected-total-hours', showCondition: hasFinancialCategoriesUpdate},
		{key: 'estimated-points', showCondition: hasFinancialCategoriesUpdate},
		{key: 'remaining-work-points', showCondition: hasFinancialCategoriesUpdate},
		{key: 'estimated-billable-value-of-service', showCondition: hasFinancialCategoriesUpdate},
		{key: 'actual-billable-value-of-service', showCondition: hasFinancialCategoriesUpdate},
		{key: 'remaining-work-billable', showCondition: hasFinancialCategoriesUpdate},
		{key: 'projected-total-billable', showCondition: hasFinancialCategoriesUpdate},
		{key: 'estimated-revenue', showCondition: hasFinancialCategoriesUpdate},
		{key: 'actual-revenue', showCondition: hasFinancialCategoriesUpdate},
		{key: 'remaining-work-revenue', showCondition: hasFinancialCategoriesUpdate},
		{key: 'projected-total-revenue', showCondition: hasFinancialCategoriesUpdate},
		{key: 'projected-over-under', showCondition: hasFinancialCategoriesUpdate},
		{key: 'approved'},
		{key: 'labels'},
		...Array.from({length: customFieldColumnCount}, (_, index) => ({key: `custom-field-${index}`})),
	];

	return columns.filter(column => column.showCondition ?? true);
};
